/**
 * Source: Figma "RM V2 Library (MUI v7.2.0)" — Theme page, `Variables Design Sheet` → `Card - palette`.
 * https://www.figma.com/design/kMeScGRhmLq2l22M2ZkmiE/RM-V2-Library--MUI-v7.2.0-?node-id=11207-164308
 *
 * Every value aliases a primitive in `./colorPrimitives`.
 *
 * Source-of-truth shape (slot-first): `lightSemantic` / `darkSemantic` mirror the
 * structure that `theme.vars.palette.rmh.*` exposes via MUI's CSS-variable wiring.
 * Consumers inside `packages/ds` should reach for `theme.vars.palette.rmh.*` —
 * the variable resolves to the right value per color scheme without an
 * `applyStyles("dark", …)` block.
 *
 * The mode-first exports below (`paper`, `border`, `text`, `icon`, etc.) are
 * derived from the slot-first source for backward compatibility with app code
 * (`apps/rmh-patient`, `apps/rmh-admin`) and the `paperAt` / `borderAt` helpers.
 * They will be removed once those callers migrate.
 *
 * Verified with design (IL - 05/08/2026).
 */
import { colorPrimitives as c } from "./colorPrimitives";

// ---------------------------------------------------------------------------
// Slot-first source of truth
// ---------------------------------------------------------------------------

type Triplet = { rest: string; hover: string; active: string };

const slot = (rest: string, hover: string, active: string): Triplet => ({
  rest,
  hover,
  active,
});

export type SemanticPalette = {
  text: {
    primary: string;
    secondary: string;
    disabled: string;
    inverse: string;
    staticWhite: string;
    staticBlack: string;
    brand: string;
    interactiveLink: string;
    success: string;
    warning: string;
    error: string;
  };
  icon: {
    primary: string;
    secondary: string;
    disabled: string;
    inverse: string;
    staticWhite: string;
    staticBlack: string;
    brand: string;
    interactiveLink: string;
    success: string;
    warning: string;
    error: string;
  };
  border: {
    "1-surface": Triplet;
    "2-dimmed": Triplet;
    "3-muted": Triplet;
    "4-faint": Triplet;
    "5-brand": Triplet;
    "6-brandMuted": Triplet;
    "7-brandFaint": Triplet;
    "8-error": Triplet;
    "9-errorFaint": Triplet;
    "10-success": Triplet;
    "11-successFaint": Triplet;
    "12-warning": Triplet;
    "13-warningFaint": Triplet;
  };
  paper: {
    "0-base": string;
    "1-surface": Triplet;
    "2-dimmed": Triplet;
    "3-muted": Triplet;
    "4-faint": Triplet;
    "5-brandFilled": Triplet;
    "6-brandMuted": Triplet;
    "7-brandFaint": Triplet;
    "8-errorFilled": Triplet;
    "9-errorFaint": Triplet;
    "10-successFilled": Triplet;
    "11-successFaint": Triplet;
    "12-warningFilled": Triplet;
    "13-warningFaint": Triplet;
    "14-staticDark": string;
  };
  paperElevation: {
    "0-base": string;
    10: string;
    11: string;
    12: string;
    13: string;
    14: string;
    15: string;
  };
  stateBackground: { disabled: string; disabledInput: string };
  stateBorder: { disabled: string };
  nativeColor: { scrollbarBg: string };
  focus: {
    primary: string;
    error: string;
    warning: string;
    success: string;
    neutral: string;
  };
};

/**
 * Quirks preserved verbatim from Figma:
 *  - border `12-warning`: light and dark identical at every state.
 *  - border `13-warningFaint` dark active jumps to amber/500 (non-monotonic).
 *  - border `2-dimmed` triplets use entirely different hues across states.
 *  - paper `3-muted` light hover === active.
 *  - paper `12-warningFilled` light hover === rest.
 *
 * Borders use un-suffixed names for the filled tiers (`5-brand`, `8-error`,
 * `10-success`, `12-warning`) — asymmetric with paper, which uses `-Filled`.
 */
export const lightSemantic = {
  text: {
    primary: c.gray[900],
    secondary: c.gray[600],
    disabled: c.gray[400],
    inverse: c.base.white,
    staticWhite: c.base.white,
    staticBlack: c.base.black,
    brand: c.blue[800],
    interactiveLink: c.blue[600],
    success: c.green[700],
    warning: c.amber[800],
    error: c.red[700],
  },
  icon: {
    primary: c.gray[900],
    secondary: c.gray[500],
    disabled: c.gray[300],
    inverse: c.base.white,
    staticWhite: c.base.white,
    staticBlack: c.base.black,
    brand: c.blue[600],
    interactiveLink: c.blue[600],
    success: c.green[600],
    warning: c.amber[600],
    error: c.red[600],
  },
  border: {
    "1-surface": slot(c.gray[200], c.blue[600], c.blue[600]),
    "2-dimmed": slot(c.gray[500], c.gray[800], c.blue[700]),
    "3-muted": slot(c.gray[300], c.gray[400], c.gray[500]),
    "4-faint": slot(c.gray[200], c.gray[400], c.blue[600]),
    "5-brand": slot(c.blue[500], c.blue[600], c.blue[700]),
    "6-brandMuted": slot(c.blue[400], c.blue[500], c.blue[600]),
    "7-brandFaint": slot(c.blue[300], c.blue[400], c.blue[500]),
    "8-error": slot(c.red[500], c.red[600], c.red[700]),
    "9-errorFaint": slot(c.red[300], c.red[400], c.red[500]),
    "10-success": slot(c.green[500], c.green[600], c.green[700]),
    "11-successFaint": slot(c.green[300], c.green[400], c.green[500]),
    "12-warning": slot(c.amber[600], c.amber[700], c.amber[800]),
    "13-warningFaint": slot(c.amber[300], c.amber[400], c.amber[500]),
  },
  paper: {
    "0-base": c.blue[50],
    "1-surface": slot(c.base.white, c.blue[50], c.blue[100]),
    "2-dimmed": slot(c.gray[400], c.gray[500], c.gray[600]),
    "3-muted": slot(c.gray[100], c.gray[200], c.gray[200]),
    "4-faint": slot(c.gray[50], c.gray[100], c.gray[100]),
    "5-brandFilled": slot(c.blue[600], c.blue[700], c.blue[800]),
    "6-brandMuted": slot(c.blue[100], c.blue[200], c.blue[200]),
    "7-brandFaint": slot(c.blue[50], c.blue[100], c.blue[100]),
    "8-errorFilled": slot(c.red[600], c.red[700], c.red[800]),
    "9-errorFaint": slot(c.red[50], c.red[100], c.red[100]),
    "10-successFilled": slot(c.green[600], c.green[700], c.green[800]),
    "11-successFaint": slot(c.green[50], c.green[100], c.green[100]),
    "12-warningFilled": slot(c.amber[600], c.amber[600], c.amber[800]),
    "13-warningFaint": slot(c.amber[50], c.amber[100], c.amber[100]),
    "14-staticDark": c.blue[900],
  },
  paperElevation: {
    "0-base": c.blue[50],
    10: c.base.white,
    11: c.base.white,
    12: c.base.white,
    13: c.base.white,
    14: c.base.white,
    15: c.base.white,
  },
  stateBackground: {
    disabled: c.gray[200],
    disabledInput: c.gray[50],
  },
  stateBorder: {
    disabled: c.gray[300],
  },
  nativeColor: {
    scrollbarBg: c.gray[200],
  },
  focus: {
    primary: "0 0 0 3px rgba(49, 91, 245, 0.32)", // blue/600 @ 32%
    error: "0 0 0 3px rgba(212, 23, 36, 0.32)", // red/600 @ 32%
    warning: "0 0 0 3px rgba(249, 121, 7, 0.32)", // amber/600 @ 32%
    success: "0 0 0 3px rgba(52, 133, 56, 0.32)", // green/600 @ 32%
    neutral: "0 0 0 3px rgba(117, 130, 148, 0.32)", // gray/500 @ 32%
  },
} as const satisfies SemanticPalette;

export const darkSemantic = {
  text: {
    primary: c.gray[100],
    secondary: c.gray[300],
    disabled: c.gray[500],
    inverse: c.base.black,
    staticWhite: c.base.white,
    staticBlack: c.base.black,
    brand: c.blue[200],
    interactiveLink: c.blue[300],
    success: c.green[200],
    warning: c.amber[200],
    error: c.red[200],
  },
  icon: {
    primary: c.gray[100],
    secondary: c.gray[500],
    disabled: c.gray[700],
    inverse: c.base.black,
    staticWhite: c.base.white,
    staticBlack: c.base.black,
    brand: c.blue[300],
    interactiveLink: c.blue[300],
    success: c.green[300],
    warning: c.amber[300],
    error: c.red[300],
  },
  border: {
    "1-surface": slot(c.gray[800], c.blue[500], c.blue[500]),
    "2-dimmed": slot(c.gray[500], c.blue[400], c.blue[400]),
    "3-muted": slot(c.gray[800], c.gray[700], c.gray[600]),
    "4-faint": slot(c.gray[600], c.blue[500], c.blue[500]),
    "5-brand": slot(c.blue[500], c.blue[400], c.blue[300]),
    "6-brandMuted": slot(c.blue[700], c.blue[600], c.blue[500]),
    "7-brandFaint": slot(c.blue[800], c.blue[700], c.blue[600]),
    "8-error": slot(c.red[500], c.red[400], c.red[300]),
    "9-errorFaint": slot(c.red[800], c.red[700], c.red[600]),
    "10-success": slot(c.green[500], c.green[400], c.green[300]),
    "11-successFaint": slot(c.green[800], c.green[700], c.green[600]),
    "12-warning": slot(c.amber[600], c.amber[700], c.amber[800]),
    "13-warningFaint": slot(c.amber[800], c.amber[700], c.amber[500]),
  },
  paper: {
    "0-base": c.base.black,
    "1-surface": slot(c.gray[900], c.base.black, c.blue[900]),
    "2-dimmed": slot(c.gray[600], c.gray[500], c.gray[400]),
    "3-muted": slot(c.gray[800], c.gray[700], c.gray[700]),
    "4-faint": slot(c.gray[900], c.gray[800], c.gray[800]),
    "5-brandFilled": slot(c.blue[600], c.blue[700], c.blue[800]),
    "6-brandMuted": slot(c.blue[800], c.blue[700], c.blue[700]),
    "7-brandFaint": slot(c.blue[900], c.blue[800], c.blue[800]),
    "8-errorFilled": slot(c.red[600], c.red[700], c.red[800]),
    "9-errorFaint": slot(c.red[900], c.red[800], c.red[800]),
    "10-successFilled": slot(c.green[600], c.green[700], c.green[800]),
    "11-successFaint": slot(c.green[900], c.green[800], c.green[800]),
    "12-warningFilled": slot(c.amber[600], c.amber[700], c.amber[800]),
    "13-warningFaint": slot(c.amber[900], c.amber[800], c.amber[800]),
    "14-staticDark": c.blue[900],
  },
  paperElevation: {
    "0-base": c.base.black,
    10: "#313131",
    11: "#313131",
    12: "#333333",
    13: "#333333",
    14: "#333333",
    15: "#333333",
  },
  stateBackground: {
    disabled: c.gray[700],
    disabledInput: c.gray[800],
  },
  stateBorder: {
    disabled: c.gray[600],
  },
  nativeColor: {
    scrollbarBg: c.gray[700],
  },
  focus: {
    primary: "0 0 0 3px rgba(107, 156, 255, 0.72)", // blue/400 @ 72%
    error: "0 0 0 3px rgba(255, 87, 98, 0.72)", // red/400 @ 72%
    warning: "0 0 0 3px rgba(255, 158, 32, 0.72)", // amber/400 @ 72%
    success: "0 0 0 3px rgba(105, 191, 108, 0.72)", // green/400 @ 72%
    neutral: "0 0 0 3px rgba(146, 159, 176, 0.72)", // gray/400 @ 72%
  },
} as const satisfies SemanticPalette;

export const semantic = { light: lightSemantic, dark: darkSemantic } as const;

// ---------------------------------------------------------------------------
// Mode-first compat layer (derived from slot-first source)
// ---------------------------------------------------------------------------
//
// Kept for backward compatibility with app callers (`apps/rmh-patient`) that still import these names. ds-internal consumers
// should use `theme.vars.palette.rmh.*` instead. Everything below should be removed when the refactor ds system is updated in onboarding V2.

export type ModePair<T> = { light: T; dark: T };
export type InteractiveTriplet = Triplet;

const lift = <T>(light: T, dark: T): ModePair<T> => ({ light, dark });

// Map each group's keys to a ModePair view of the slot-first source.
// Triplet tiers preserve their `{ rest, hover, active }` shape inside each
// mode, matching the original `paper["1-surface"].light.rest` access pattern.
const liftGroup = <L extends Record<string, unknown>>(
  l: L,
  d: { [K in keyof L]: unknown }
): { [K in keyof L]: ModePair<L[K]> } => {
  const out = {} as { [K in keyof L]: ModePair<L[K]> };
  for (const key of Object.keys(l) as (keyof L)[]) {
    out[key] = lift(l[key], d[key] as L[typeof key]);
  }
  return out;
};

export const text = liftGroup(lightSemantic.text, darkSemantic.text);
export const icon = liftGroup(lightSemantic.icon, darkSemantic.icon);
export const border = liftGroup(lightSemantic.border, darkSemantic.border);
export const paper = liftGroup(lightSemantic.paper, darkSemantic.paper);
export const paperElevation = liftGroup(
  lightSemantic.paperElevation,
  darkSemantic.paperElevation
);
export const stateBackground = liftGroup(
  lightSemantic.stateBackground,
  darkSemantic.stateBackground
);
export const stateBorder = liftGroup(
  lightSemantic.stateBorder,
  darkSemantic.stateBorder
);
export const nativeColor = liftGroup(
  lightSemantic.nativeColor,
  darkSemantic.nativeColor
);
export const focus = liftGroup(lightSemantic.focus, darkSemantic.focus);

export const colorSemantic = {
  text,
  icon,
  border,
  paper,
  paperElevation,
  stateBackground,
  stateBorder,
  nativeColor,
  focus,
} as const;
